import { z } from 'zod';
import { phoneSchema } from './lead';
import { HONEYPOT_FIELD_NAME, honeypotFieldSchema } from './honeypot';
import { attributionSchema, istSteuerzeichen } from './attribution';
import { avmDossierSchema } from './avm-dossier';
/**
 * Immobilien-Bewertung (M0 shared contracts) — the map + valuation funnel for
 * the hybrid MLG Immobilien / Finavest tool. Shared so the geo pipeline (M1),
 * the map UI (M2) and the backend (M3) speak the exact same contracts.
 *
 * PII-minimisation is a hard rule: the public EVENTS carry only the Gemeinde
 * (BFS Gemeindenummer), never a street address, and `location` — what the AVM
 * provider sees — stays on Gemeinde/PLZ level. Since owner 7-10 (order ADR)
 * the intake carries the address the visitor PICKED in the funnel as its own
 * field (`adresse`, {@link valuationFunnelAdresseSchema}) — for the CRM only:
 * stored in `valuation_requests.funnelAdresse`, never in an event, the audit
 * trail, the provider call or the customer report. The request is signed with
 * the canonical HMAC (`signRequest` over `${ts}.${body}` + the `x-finavest-*`
 * headers, see crypto/hmac) — do NOT invent a second signing scheme.
 */
/**
 * Brand discriminator for the multi-tenant tool (MLG Immobilien vs. Finavest).
 *
 * FUNNEL BRANDS — not "every tenant the platform has". This list gates the
 * PUBLIC ingest route (`POST /ingest/valuation` via `valuationRequestSchema`)
 * and the two valuation events, and `ingest.service.ts` turns it into a lead
 * SOURCE with a binary expression (`=== 'finavest' ? … : 'mlg_valuation'`). A
 * third value here would therefore not merely be *accepted*: it would be
 * silently attributed to the MLG funnel and persisted. That is why S-5 put the
 * holding realm in `MEMBERSHIP_TENANTS` (schemas/crm.ts) rather than widening
 * this list — see DRIP-ONTVLECHTING.md §6 — and why `gu` went the same way when
 * the BAU test phase needed a membership route into it. A membership is not a
 * funnel brand. Anything added HERE has to make that expression total first.
 */
export const TENANTS = ['mlg', 'finavest'];
export const tenantSchema = z.enum(TENANTS);
/**
 * Optionele objectkenmerken (order B, owner 28-8) — de klasse-3-velden uit de
 * IAZI-contract-audit (docs/IAZI-CONTRACT-AUDIT.md §3): wat het model kent en
 * waarde geeft, maar het formulier tot nu toe niet vroeg. STRICT: een onbekende
 * sleutel is een fout, geen stille drop — dat is de zod-strict-pin die nieuwe
 * backend-velden verplicht dragen. Alles optioneel («weiss ich nicht» = weg-
 * laten; het model vult dan zelf in en meldt dat via `replaceCode`).
 *
 * Naar IAZI (avm.iazi.ts): haustyp → typeHouseCode 1..5 · wohnungstyp →
 * typeFlatCode 1..4 · grundstueck → surfaceGround m² · lift → liftNb 0/1 ·
 * maisonette → maisonetteFlag 0/1 · gebaeudetyp → typeFlatBuildingCode 1..2
 * (micro-6, offered + paid a3). GEEN adres, GEEN EGID, GEEN coördinaten:
 * die blijven client-side (GWR-voorvulling in de browser; juristenpoort
 * Ziffer 14). Welke sleutel bij welk objecttype hoort, bewaakt
 * {@link valuationObjectSchema}.
 */
export const HAUSTYPEN = ['freistehend', 'doppel', 'reihe_mitte', 'reihe_eck', 'terrasse'];
export const WOHNUNGSTYPEN = ['etage', 'penthouse', 'attika', 'eg_garten'];
/** German labels of the two type vocabularies — ONE place for the Bewertung form,
 *  the Verkaufsmandat Checkliste and the prefill between them (B3-4, 16-9). */
export const HAUSTYP_LABEL = {
    freistehend: 'Freistehend',
    doppel: 'Doppel-EFH',
    reihe_mitte: 'Reihenhaus (Mitte)',
    reihe_eck: 'Reihenhaus (Eck)',
    terrasse: 'Terrassenhaus',
};
/**
 * Micro-6 punt 0 (18-9): the WORDS are IAZI's (Web Tool «Wohnungstyp»), the
 * KEYS stay the spec's English names — `typeFlatCode` 2 «Penthouse» is what
 * IAZI calls «Attikawohnung», 3 «Attic apartment» is «Dachwohnung». Until
 * 18-9 the words stood the other way round, so «Attika» sent code 3
 * (Dachwohnung). Keys/codes unchanged (stored rows keep their meaning); the
 * word↔code pair is pinned in avm.iazi.contract.test.ts.
 */
export const WOHNUNGSTYP_LABEL = {
    etage: 'Etagenwohnung',
    penthouse: 'Attikawohnung',
    attika: 'Dachwohnung',
    eg_garten: 'EG mit Garten',
};
/** EWG: Gebäudetyp der Wohnung — IAZI `typeFlatBuildingCode` 1 Mehrfamilienhaus · 2 Terrassenhaus (micro-6). */
export const GEBAEUDETYPEN = ['mfh', 'terrassenhaus'];
export const GEBAEUDETYP_LABEL = {
    mfh: 'Mehrfamilienhaus',
    terrassenhaus: 'Terrassenhaus',
};
/**
 * B8 (owner 29-9): the German NAME of every optional Merkmal — the row label in
 * the Bewertungsbericht («Ihre Angaben»), next to the value labels above. One
 * table, so the PDF and the responsive Bericht-Ansicht cannot word a field
 * differently. Keys = {@link objektMerkmaleSchema}'s keys (pinned in the test).
 */
export const OBJEKT_MERKMAL_LABEL = {
    haustyp: 'Haustyp',
    wohnungstyp: 'Wohnungstyp',
    grundstueck: 'Grundstück',
    lift: 'Lift',
    maisonette: 'Maisonette',
    gebaeudetyp: 'Gebäudetyp',
};
/**
 * RV620 n3 (master 25-9 14:10): the ceiling of a Grundstückfläche, ONE constant — this schema,
 * the Bewertungsformular's clamp and the Checkliste hint read it. OUR bound: the IAZI spec sets
 * none on `surfaceGround`.
 */
export const GRUNDSTUECK_MAX = 1_000_000;
export const objektMerkmaleSchema = z
    .object({
    /** EFH: freistehend · Doppel-EFH · Reihen-Mitte · Reihen-Eck · Terrassenhaus. */
    haustyp: z.enum(HAUSTYPEN).optional(),
    /** EWG: Etagenwohnung · Attikawohnung · Dachwohnung · EG mit Garten (words = IAZI's, keys = spec). */
    wohnungstyp: z.enum(WOHNUNGSTYPEN).optional(),
    /** EFH: Grundstückfläche in m². */
    grundstueck: z.number().positive().max(GRUNDSTUECK_MAX).optional(),
    /** EWG: Lift im Gebäude. */
    lift: z.boolean().optional(),
    /** EWG: Wohnung über mehrere Geschosse (GWR `WMEHRG`). */
    maisonette: z.boolean().optional(),
    /** EWG: Gebäudetyp der Wohnung — Mehrfamilienhaus | Terrassenhaus (IAZI `typeFlatBuildingCode`, offered + paid a3; micro-6). */
    gebaeudetyp: z.enum(GEBAEUDETYPEN).optional(),
})
    .strict();
/**
 * Which optional Merkmale belong to which Objekttyp — the applicability pin.
 *
 * A field may belong to MORE THAN ONE type (22-9): the Grundstückfläche is a
 * core figure for a house AND for an apartment building, and the paid a1 (MFH)
 * knows `liftNb` where the paid a3 (EWG) does not. The check below therefore
 * compares against the ALLOWED list of the chosen type; the earlier form
 * («forbidden = the union of the other lists») could not express a shared field
 * and silently refused `grundstueck`/`lift` on an MFH.
 */
export const MERKMALE_EFH = ['haustyp', 'grundstueck'];
export const MERKMALE_EWG = ['wohnungstyp', 'lift', 'maisonette', 'gebaeudetyp'];
export const MERKMALE_MWG = ['grundstueck', 'lift'];
/**
 * Plausibility bounds for a construction year — the SAME bounds on every side
 * (server schema, CRM form, public widget mirror), because a year outside them
 * is never a real object and IAZI does not refuse it: the first real e2e (16-9)
 * sent `baujahr: 1000` (a too-short entry the CRM form silently clamped to its
 * floor of 1000) and IAZI valued a thousand-year-old house at half its worth
 * without a single warning that pointed at the year. Lower bound 1500: the
 * oldest inhabited Swiss houses date from the 13th century, but a 17th/18th-
 * century farmhouse (Simmental!) must pass; upper bound: current year plus a
 * short planning horizon (Neubau).
 */
export const BAUJAHR_MIN = 1500;
export const BAUJAHR_VORAUS = 2;
export const baujahrMax = (jetzt = new Date()) => jetzt.getFullYear() + BAUJAHR_VORAUS;
/**
 * Zustand des Gebäudes (order B3-2, 16-9) — IAZI's `buildCondition` scale as
 * the Web Tool words it: Sehr gut 1.0 · Sehr gut–Gut 1.5 · Gut 2.0 ·
 * Gut–Mittel 2.5 · Mittel 3.0 · Mittel–Schlecht 3.5 · Schlecht 4.0 (LOWER is
 * better; the served spec allows the half steps). Until B3-2 the form offered
 * the QUALITY words (Einfach/Normal/Gehoben/Luxus) for the condition as well:
 * one list fed two different model inputs, and «renovationsbedürftig» had no
 * word at all. Those four words stay accepted as LEGACY values (stored rows;
 * an old CRM bundle during the rollout window) and map onto exactly the 1…4
 * the adapter always sent for them (`CONDITION_RATING`, 28-8 audit) — pinned
 * explicitly, never offered again. «Ausbaustandard» keeps Einfach…Luxus →
 * `buildQuality` (avm.ts).
 *
 * NAMING (#576 MINOR-3): this is one of THREE condition vocabularies in
 * `@finavest/shared` — `ZUSTAND` (schemas/pipeline: the Checkliste words
 * neuwertig/renoviert/gepflegt/renovationsbeduerftig) and `ZUSTAND_LABELS`
 * (schemas/expose: Exposé texts) are the other two. The only bridge between
 * them is `mandat-bewertung.ts` (`MANDAT_ZUSTAND`, pinned). A rename to
 * `OBJEKT_ZUSTAND…` was weighed and not done: B3-2 code on main and the
 * B3-3/B3-4 layers in flight read these names; the note is the guard.
 */
export const ZUSTAND_STUFEN = [
    'sehr_gut',
    'sehr_gut_gut',
    'gut',
    'gut_mittel',
    'mittel',
    'mittel_schlecht',
    'schlecht',
];
/** Legacy condition words (the quality vocabulary) — still accepted, never offered. */
export const ZUSTAND_ALT = ['einfach', 'normal', 'gehoben', 'luxus'];
export const ZUSTAENDE = [...ZUSTAND_STUFEN, ...ZUSTAND_ALT];
/** Zustand → IAZI `buildCondition` (1.00 Very good … 4.00 Poor, half steps). */
export const ZUSTAND_BUILD_CONDITION = {
    sehr_gut: 1,
    sehr_gut_gut: 1.5,
    gut: 2,
    gut_mittel: 2.5,
    mittel: 3,
    mittel_schlecht: 3.5,
    schlecht: 4,
    // legacy — exactly the numbers the adapter sent for these words since 28-8
    luxus: 1,
    gehoben: 2,
    normal: 3,
    einfach: 4,
};
export const ZUSTAND_LABEL = {
    sehr_gut: 'Sehr gut',
    sehr_gut_gut: 'Sehr gut – Gut',
    gut: 'Gut',
    gut_mittel: 'Gut – Mittel',
    mittel: 'Mittel',
    mittel_schlecht: 'Mittel – Schlecht',
    schlecht: 'Schlecht',
    luxus: 'Luxus',
    gehoben: 'Gehoben',
    normal: 'Normal',
    einfach: 'Einfach',
};
export const istZustand = (v) => typeof v === 'string' && ZUSTAENDE.includes(v);
/** The model rating for an entered/stored Zustand; `undefined` for anything else. */
export const zustandRating = (v) => istZustand(v) ? ZUSTAND_BUILD_CONDITION[v] : undefined;
/** Display label; an unknown stored value is shown as it is — never invented. */
export const zustandLabel = (v) => istZustand(v) ? ZUSTAND_LABEL[v] : (v ?? '');
/** Object characteristics the hedonic/AVM valuation step needs — the shape (IAZI-order 8-10 A1:
 *  the CRM's paid DTO extends it with an optional `raeume` for the MFH, see `immoBewertungCreateSchema`). */
export const valuationObjectBaseSchema = z.object({
    /** efh = Einfamilienhaus, ewg = Eigentumswohnung, mwg = Mehrfamilienhaus. */
    objektTyp: z.enum(['efh', 'ewg', 'mwg']),
    /** Wohnfläche in m². */
    wohnflaeche: z.number().positive().max(100_000),
    /** Anzahl Zimmer — fractional allowed (Swiss "4.5 Zimmer"). */
    raeume: z.number().positive().max(100),
    baujahr: z.number().int().min(BAUJAHR_MIN).max(2100),
    /** Zustand des Gebäudes — {@link ZUSTAND_STUFEN}, plus the legacy words. */
    zustand: z.enum(ZUSTAENDE).optional(),
    /** Order B: optional class-3 characteristics, see {@link objektMerkmaleSchema}. */
    merkmale: objektMerkmaleSchema.optional(),
});
/** The refinement of {@link valuationObjectBaseSchema} — one place for every schema built on it. */
export function pruefeObjekt(o, ctx) {
    // Upper bound is a function of today (Neubau horizon), so it lives here
    // and not in the static `.max()` above.
    if (o.baujahr > baujahrMax()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['baujahr'],
            message: `baujahr liegt nach ${baujahrMax()}`,
        });
    }
    // A Haustyp on a flat or a Lift on a house is not "harmless extra": the
    // adapter would have to guess which model field it means. Refuse instead.
    const m = o.merkmale;
    if (!m)
        return;
    const toegestaan = o.objektTyp === 'efh' ? MERKMALE_EFH : o.objektTyp === 'ewg' ? MERKMALE_EWG : MERKMALE_MWG;
    // De volledige veldenlijst komt uit het schema zelf, niet uit een handlijst:
    // een veld dat later aan `objektMerkmaleSchema` wordt toegevoegd is dan
    // meteen gedekt in plaats van stil overal toegestaan.
    const alle = Object.keys(objektMerkmaleSchema.shape);
    for (const k of alle) {
        if (!toegestaan.includes(k) && m[k] !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['merkmale', k],
                message: `${k} gilt nicht für objektTyp ${o.objektTyp}`,
            });
        }
    }
}
/** Object characteristics the hedonic/AVM valuation step needs. */
export const valuationObjectSchema = valuationObjectBaseSchema.superRefine(pruefeObjekt);
/**
 * Location at Gemeinde granularity only — never a raw address in the event.
 *
 * `gemeindeBfs` is the official BFS Gemeindenummer: OUR identifier, used by the
 * lead, the CRM and the map. `plz` is the AVM provider's: its public (offered)
 * contract resolves a property by POSTCODE, so this is the field that actually
 * locates the valuation — not a display aid, and the funnel is required to send
 * it (avm.iazi.ts reads `location.plz` and nothing else on that path). `label`
 * is the only coarse display aid here.
 *
 * A postcode is still Gemeinde-level precision, not an address: the street
 * stays deliberately outside this schema — `location` is what the provider's
 * public (offered) call is built from, and the street there is the internal
 * `paid` product. The address the visitor picked travels BESIDE it since owner
 * 7-10 (`adresse`, {@link valuationFunnelAdresseSchema}), for the CRM only;
 * sending it to the provider is a separate decision nobody has taken.
 */
export const valuationLocationSchema = z.object({
    gemeindeBfs: z.number().int().positive(),
    plz: z.string().trim().max(10).optional(),
    label: z.string().trim().max(120).optional(),
});
/**
 * B8 (owner 29-9): the STANDORT-SNAPSHOT of a funnel request — what the widget
 * knew about the object when the visitor pressed «Weiter», so the
 * Bewertungsbericht can show every input WITH its origin («Ihre Eingabe» /
 * «Gebäuderegister GWR») and the building facts the register proposed.
 *
 * NEVER A STREET, A HOUSE NUMBER, AN EGID OR A COORDINATE (rule master 29-9):
 * the report's map is on Gemeinde level, and storing the EGID is a separate
 * owner decision (register item). `.strict()` on every level is the pin: an
 * unknown key — `strasse`, `egid`, `lat` — is a 400, never a silent drop. The
 * GWR facts are the non-personal building characteristics the IAZI contract
 * audit already allows on the backend (§5: Baujahr, Geschosse, Wohnungen …).
 *
 * `herkunft` per field: `gwr` when the value is the GWR prefill and the
 * visitor did not change it, `eingabe` otherwise — the widget knows (its
 * «aus dem Gebäuderegister» badge is exactly that fact). The Baujahr has a
 * third word (RVB8 n4): `gwr_bauperiode` when the register knew only the
 * PERIOD and the proposal is its midpoint — a register value, but a derived
 * one, and the report says so.
 */
export const HERKUNFT_WERTE = ['gwr', 'eingabe'];
const herkunftSchema = z.enum(HERKUNFT_WERTE);
export const HERKUNFT_BAUJAHR_WERTE = ['gwr', 'gwr_bauperiode', 'eingabe'];
/** GWR `GBAUP` codes: 8011 (vor 1919) … 8023 (nach 2015). */
export const GWR_BAUPERIODE_MIN = 8011;
export const GWR_BAUPERIODE_MAX = 8023;
export const valuationStandortSnapshotSchema = z
    .object({
    gemeindeName: z.string().trim().min(1).max(120),
    kanton: z.string().regex(/^[A-Z]{2}$/),
    gwr: z
        .object({
        /** `GBAUJ` — the exact year the register knows. */
        baujahr: z.number().int().min(BAUJAHR_MIN).max(2100).optional(),
        /** `GBAUP` — the period code. */
        bauperiode: z.number().int().min(GWR_BAUPERIODE_MIN).max(GWR_BAUPERIODE_MAX).optional(),
        /** `GASTW` — Anzahl Geschosse. */
        geschosse: z.number().int().min(1).max(200).optional(),
        /** `GANZWHG` — Anzahl Wohnungen im Gebäude. */
        wohnungen: z.number().int().min(1).max(1000).optional(),
        /** `GAREA` — Gebäudegrundfläche in whole m² (the register keeps an integer; RVB8 n2). */
        garea: z.number().int().positive().max(1_000_000).optional(),
        /** `GKLAS` — Gebäudeklasse (4-digit code). */
        gebaeudeklasse: z.number().int().min(1000).max(1999).optional(),
    })
        .strict()
        .optional(),
    herkunft: z
        .object({
        baujahr: z.enum(HERKUNFT_BAUJAHR_WERTE),
        wohnflaeche: herkunftSchema,
        raeume: herkunftSchema,
        maisonette: herkunftSchema.optional(),
    })
        .strict(),
})
    .strict();
/**
 * Owner 7-10 (order ADR): the ADDRESS the visitor picked on the funnel's
 * address step (geo.admin.ch autocomplete) — the street with its house number,
 * the PLZ and the Ort. Kept for the CRM, so the advisor can look at the
 * building before he calls: `valuation_requests.funnelAdresse`, the lead card
 * (with a Google Maps link), the team mails 1/2 and 2/2 (`objekt_adresse`) and,
 * on «Übernehmen», the dossier: its `addressLine` (one display line) and its own
 * `funnelAdresse` (the structure «Verkaufsmandat anlegen» reads, RV661 MAJOR-1).
 *
 * NEVER to the AVM provider — the public estimate stays on PLZ level
 * ({@link valuationLocationSchema}; sending the street there is a separate,
 * open decision) — and never into an event, the audit trail, the dataLayer or
 * the customer report. The manual Gemeinde mode has no address and sends none.
 *
 * STRICT like every funnel bag: nothing else of the geo.admin record — no
 * coordinate, no feature id, no EGID, no label — and an unknown key is a 400,
 * never a silent drop. Street and Ort refuse a control character or a lone
 * surrogate half ({@link istSaubereAdressangabe}): Postgres refuses the whole
 * jsonb row for the latter, and the team mail's text variant is line-based.
 */
export const FUNNEL_ADRESSE_STRASSE_MAX = 120;
export const FUNNEL_ADRESSE_ORT_MAX = 80;
/** No `verboten` character and no surrogate half without its partner. */
function zeichenErlaubt(s, verboten) {
    for (let i = 0; i < s.length; i += 1) {
        const c = s.charCodeAt(i);
        if (c >= 0xd800 && c <= 0xdbff) {
            const n = s.charCodeAt(i + 1);
            if (n >= 0xdc00 && n <= 0xdfff) {
                i += 1;
                continue;
            }
            return false;
        }
        if (c >= 0xdc00 && c <= 0xdfff)
            return false;
        if (verboten(c))
            return false;
    }
    return true;
}
/**
 * A street or Ort as it may be stored: none of the house set of control and
 * invisible format characters ({@link istSteuerzeichen}) and no lone surrogate
 * half. Letters, accents, digits, the apostrophe, the slash — and a whole emoji
 * — pass: the mail escapes HTML, React escapes the card.
 */
export function istSaubereAdressangabe(s) {
    return zeichenErlaubt(s, istSteuerzeichen);
}
export const valuationFunnelAdresseSchema = z
    .object({
    /** Street and house number, as the pick names them («Tramstrasse 10»). */
    strasse: z
        .string()
        .trim()
        .min(1)
        .max(FUNNEL_ADRESSE_STRASSE_MAX)
        .refine(istSaubereAdressangabe, 'strasse: Steuerzeichen oder halbes Surrogat'),
    /** Swiss postcode, exactly four digits. */
    plz: z.string().regex(/^\d{4}$/),
    ort: z
        .string()
        .trim()
        .min(1)
        .max(FUNNEL_ADRESSE_ORT_MAX)
        .refine(istSaubereAdressangabe, 'ort: Steuerzeichen oder halbes Surrogat'),
})
    .strict();
/**
 * «Tramstrasse 10, 8050 Zürich» — the ONE line of the address: the CRM card and
 * its Google Maps query, the team mails, the dossier's `addressLine`.
 */
export const funnelAdresseZeile = (a) => `${a.strasse}, ${a.plz} ${a.ort}`;
/**
 * A stored `funnelAdresse` read back: the address when it has exactly the
 * strict shape, else `null` — a row from before 7-10, the manual mode, or
 * anything that is not an address. One reader for the lead card, the team
 * mails and «Übernehmen».
 */
export function leseFunnelAdresse(v) {
    const r = valuationFunnelAdresseSchema.safeParse(v);
    return r.success ? r.data : null;
}
/** Contact + explicit revDSG consent (privacy policy must be accepted). */
export const valuationContactSchema = z.object({
    first: z.string().trim().min(1).max(100),
    last: z.string().trim().min(1).max(100),
    email: z.string().trim().toLowerCase().email().max(254),
    /**
     * Optional at intake — the funnel collects + verifies the phone AFTER the
     * lead exists, via the SMS-OTP step (M5). Reuses the canonical
     * {@link phoneSchema} (no new format). PII: never enters the public event.
     */
    phone: phoneSchema.optional(),
    consent: z.literal(true),
});
/**
 * Public valuation intake payload (mirrors `leadIngestSchema`'s shape and
 * signing). PII-minimisation at the edge: `location` and every event stay
 * address-free; the address the visitor picked travels only in `adresse`
 * (owner 7-10, for the CRM). The top level is not strict — an unknown key is
 * dropped (see the honeypot note below); `adresse` itself is.
 */
export const valuationRequestSchema = z.object({
    tenant: tenantSchema,
    object: valuationObjectSchema,
    location: valuationLocationSchema,
    contact: valuationContactSchema,
    /**
     * Campaign attribution: only the known keys, with cleaned values
     * (schemas/attribution.ts, RV616 m5). Personal data under the privacy text.
     */
    utm: attributionSchema,
    /** Which surface triggered the request (e.g. 'map_cta', 'value_widget'). */
    triggerSource: z.string().trim().max(120).optional(),
    /**
     * B8: the Standort-Snapshot — see {@link valuationStandortSnapshotSchema}.
     * Optional: an older widget bundle sends none, and the report then simply
     * has no Herkunft column and no register block.
     */
    standort: valuationStandortSnapshotSchema.optional(),
    /**
     * Owner 7-10 (order ADR): the address the visitor picked — see
     * {@link valuationFunnelAdresseSchema}. Optional: the manual mode and an older
     * widget send none.
     */
    adresse: valuationFunnelAdresseSchema.optional(),
});
/**
 * Public transport DTO for `POST /ingest/valuation` (M3) — the signed body the
 * backend re-validates. Wraps {@link valuationRequestSchema} and adds the edge
 * concerns, mirroring `leadIngestSchema`:
 *  - `turnstileToken` (verified server-side, same as /ingest/lead),
 *  - `consentVersion` — the revDSG wording version for the funnel
 *    `privacy_policy` consent (same const family as the lead gate's
 *    `consent.textVersion`; `contact.consent` is just the literal `true`),
 *  - `crossSellConsent` — a SEPARATE, explicit MLG↔Finavest hand-over consent.
 *    MLG and Finavest are distinct controllers, so cross-sell can NOT ride on
 *    the funnel privacy consent or on marketing; it is its own opt-in and gates
 *    the `cross_tenant_referral` consent row + the event's `crossSell` flag.
 */
export const valuationIngestSchema = valuationRequestSchema.extend({
    /** Cloudflare Turnstile token (verified server-side). */
    turnstileToken: z.string().min(1).max(4096),
    /** Version tag of the funnel consent wording shown to the user (revDSG). */
    consentVersion: z.string().trim().min(1).max(64).optional(),
    crossSellConsent: z
        .object({
        granted: z.literal(true),
        textVersion: z.string().trim().min(1).max(64),
    })
        .optional(),
    /**
     * Honeypot — see {@link honeypotFieldSchema}. The backend ignores it. The key
     * is COMPUTED from the shared constant (review #491 MINOR-4): renaming the
     * field in one place can no longer leave the schema behind.
     */
    [HONEYPOT_FIELD_NAME]: honeypotFieldSchema,
    // NO `testlauf` FLAG (review #490 MAJOR-2). Test mode is decided SERVER-SIDE
    // from LEAD_TEST_DOMAINS and nowhere else: a client-controlled flag switched
    // off the SMS-OTP gate, the per-phone cap and the SMS day budget, which is
    // the only proof that a human with a phone is behind the request. An unknown
    // key is dropped by the schema, so an old client that still sends it is fine
    // — the flag simply has no effect.
});
/**
 * Internal n8n → backend contact lookup (M3). The public
 * `valuation.lead.created` event is PII-free, so n8n resolves the
 * confirmation-mail recipient by `leadId` over an HMAC-signed internal route
 * (never on a public surface). Keeps the email out of the event store.
 */
export const valuationContactLookupSchema = z.object({
    leadId: z.string().uuid(),
    tenant: tenantSchema,
});
/** Backend → n8n contact reply for {@link valuationContactLookupSchema}. */
export const valuationContactReplySchema = z.object({
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    locale: z.string(),
});
/**
 * Backend → n8n reply for POST /internal/valuation/result-mail (E8): the
 * contact for the customer result mail, over ONE HMAC-signed hop and never
 * over the event bus.
 *
 * GEEN PDF MEER, EN GEEN DODE VELDEN ERVOOR (review #492 MINOR-8). Fase B3
 * haalde de bijlage uit de mail — de klant opent het rapport via de
 * «Bericht öffnen»-link. De optionele `pdfFilename`/`pdfBase64` bleven daarna
 * staan zonder producent en zonder lezer; zolang ze bestaan kan een latere tak
 * er weer een PDF de klantmail in duwen zonder dat één poort dat merkt. Weg
 * dus: wie de bijlage terug wil, voegt het veld bewust opnieuw toe.
 */
export const valuationResultMailReplySchema = valuationContactReplySchema;
// ── Fase B (owner 26-8): bandbreedte op het scherm, rapport via token ────────
/**
 * What the result step returns to the BROWSER after the OTP (B1). A band from
 * the provider's interval (see `bandbreite()`), never the point value: that
 * stays in the database and the PDF the mail links to. The strict shape is
 * the wire pin — `estimate`/`low`/`high` cannot ride along by accident.
 */
export const valuationBandResultSchema = z
    .object({
    bandLow: z.number().nonnegative(),
    bandHigh: z.number().nonnegative(),
    currency: z.string().min(1),
    disclaimer: z.string().min(1),
    attribution: z.string().min(1),
    /** The exact report goes to this address (masked for display: a****@x.ch). */
    emailHint: z.string().min(1),
    /**
     * B3-5b: IAZI's verdict in customer words when it is indicative (1 extra-
     * polated · 4 substituted data) — `IAZI_QUALITAET_HINWEIS_OEFFENTLICH`.
     * Absent for 2/3 and for the stub. Text only; the band stays the amount.
     */
    hinweis: z.string().min(1).max(300).optional(),
})
    .strict();
/**
 * POST /ingest/bericht/oeffnen — the edge exchanges the mail link's token for
 * the PDF. Changes nothing about the lead, so a page may call it on load.
 */
export const berichtOeffnenSchema = z.object({
    /** base64url(32 bytes) = 43 chars today; the alphabet pin rejects junk at the edge. */
    token: z
        .string()
        .min(32)
        .max(128)
        .regex(/^[A-Za-z0-9_-]+$/),
});
export const berichtOeffnenReplySchema = z.object({
    filename: z.string().min(1).max(120),
    /** A one-pager is ~50 kB; the cap (≈6 MB raw) keeps a Worker from decoding a runaway body. */
    pdfBase64: z.string().min(1).max(8_000_000),
});
/**
 * POST /ingest/bericht/bestaetigen — the SAME token, the double-opt-in leg
 * (review #492 MAJOR-1).
 *
 * WAAROM DIT EEN TWEEDE ROUTE IS. De link in de mail is een gewone `<a href>`,
 * en elke JS-uitvoerende link-scanner doet daar een GET op. Zolang uitleveren
 * en bevestigen één aanroep waren, moest de pagina wel ruilen bij page-load —
 * en bevestigde zo'n scanner namens de klant. Deze route hoort uitsluitend aan
 * een echte handeling (de knop «Bericht herunterladen»): scanners drukken
 * geen knoppen.
 */
export const berichtBestaetigenSchema = berichtOeffnenSchema;
export const berichtBestaetigenReplySchema = z
    .object({
    /** True iff THIS call was the lead's double-opt-in (exactly once per lead). */
    bestaetigt: z.boolean(),
})
    .strict();
// ── M5: SMS-OTP phone verification + instant AVM result ──────────────────────
/**
 * Phone-verification request (POST /ingest/valuation/verify-phone). The funnel
 * first creates the lead via /ingest/valuation, then posts the user's phone
 * here to receive a 6-digit OTP by SMS. `phone` reuses the canonical
 * {@link phoneSchema} — no new format, no PII in any event/edge log.
 */
/**
 * Bare hostname (no scheme, port or path; single-label hosts such as
 * `localhost` allowed). The widget's page host travels with verify-phone in
 * this shape so the OTP SMS can end in an ORIGIN-BOUND last line
 * (`@host #code` — WICG sms-one-time-codes, Apple's domain-bound codes,
 * Chrome's WebOTP). Advisory by construction: the backend echoes it into an
 * SMS only after matching it against its own allowlist, never verbatim.
 */
export const pageHostSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(253)
    .regex(/^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?)*$/);
export const valuationVerifyPhoneSchema = z.object({
    leadId: z.string().uuid(),
    phone: phoneSchema,
    /** Host of the page the visitor is on (top-level document) — see {@link pageHostSchema}. */
    pageHost: pageHostSchema.optional(),
});
/**
 * Narrow reply for verify-phone (mirrors {@link valuationContactReplySchema}'s
 * intent — never signal more than `sent`, so a wrong/known number cannot be
 * enumerated through the response).
 */
export const valuationVerifyPhoneReplySchema = z.object({
    sent: z.boolean(),
});
/**
 * OTP confirmation (POST /ingest/valuation/confirm-otp). A correct code marks
 * the phone verified, runs the AVM and returns the {@link valuationBandResultSchema}.
 * The code is exactly six digits (see the backend OTP generator); it is NEVER
 * logged — not even by the SMS stub.
 */
export const valuationConfirmOtpSchema = z.object({
    leadId: z.string().uuid(),
    otp: z.string().regex(/^[0-9]{6}$/, 'otp must be 6 digits'),
});
/**
 * B11 (IAZI 29-9, M. Molteni): the bounds of the hints IAZI's answer may carry
 * (answer 11: «Die Response kann entsprechende Hinweise enthalten, z.B.
 * „Mietwert nachhaltig scheint plausibel"»). A CHOICE of ours, not a contract
 * value — the same 200-character echo bound as the model diagnostic, and ten
 * hints are more than any measured answer carried.
 */
export const IAZI_HINWEIS_MAX = 10;
export const IAZI_HINWEIS_MAX_TEKENS = 200;
/** `<` `>` `&` `"` and the backtick — the HTML-significant characters a stored hint never carries. */
const HINWEIS_HTML_ZEICHEN = new Set([0x3c, 0x3e, 0x26, 0x22, 0x60]);
/**
 * A character a stored hint may NOT carry (RV-B11 MINOR-2): the house set of
 * control and invisible format characters — {@link istSteuerzeichen}
 * (attribution.ts: C0, DEL, C1, U+00AD, U+061C, U+200B, LRM/RLM, the line and
 * paragraph separators, the bidi embeddings/overrides/isolates, U+2060–U+2064,
 * the BOM) — plus the HTML-significant ones. ONE predicate for the adapter's
 * scrubber (`avm.iazi.ts schrubbeHinweis`) and {@link istSaubererHinweis}.
 * Char codes, not a regex class (RV-B11 n-2: the reason `pgSafeText` gives).
 */
export function istVerbotenesHinweisZeichen(c) {
    return istSteuerzeichen(c) || HINWEIS_HTML_ZEICHEN.has(c);
}
/**
 * A hint as it may be stored: no {@link istVerbotenesHinweisZeichen} and no
 * surrogate half without its partner — a lone half makes Postgres refuse the
 * whole jsonb value (22P02, measured by RV-B11 on the own PG), and that write
 * comes AFTER the paid IAZI call.
 */
export function istSaubererHinweis(s) {
    for (let i = 0; i < s.length; i += 1) {
        const c = s.charCodeAt(i);
        if (c >= 0xd800 && c <= 0xdbff) {
            const n = s.charCodeAt(i + 1);
            if (n >= 0xdc00 && n <= 0xdfff) {
                i += 1;
                continue;
            }
            return false;
        }
        if (c >= 0xdc00 && c <= 0xdfff)
            return false;
        if (istVerbotenesHinweisZeichen(c))
            return false;
    }
    return true;
}
/**
 * B11 (IAZI 29-9, answer 9): the inputs we may approximate ourselves — only the
 * a1 volume; `volume_begrenzt` when that approximation hit the upper bound
 * (RV-B11 MINOR-3, `avm.iazi.ts VOLUMEN_NAEHERUNG_MAX`). Never alone.
 */
export const IAZI_NAEHERUNGEN = ['volume', 'volume_begrenzt'];
/**
 * B3-5 (16-9): IAZI's `dataInfo` — WHICH inputs it replaced by defaults
 * (`replaceCode`) and WHICH plausibility checks fired (`checkingCode`).
 * `replaced`/`checks` are names only, shape-guarded, never a value: the same
 * guarantee as the `iazi_avm_quality` log line (docs/IAZI-CONTRACT-AUDIT.md).
 * B11 adds IAZI's hint TEXTS (`hinweise`, bounded and scrubbed — they stay out
 * of the log line, which only counts them) and our own approximations
 * (`naeherungen`). Persisted as `avmDataInfo`; the CRM renders it as
 * «Ersetzte Angaben» / «Kritische Angaben» / «Prüfhinweise IAZI». The two
 * unknown counts stay apart (a substituted field is not a flagged one).
 *
 * RV-B11 n-1: this schema DESCRIBES the stored shape; the write path does not
 * parse against it. The guard on the write path is the adapter itself
 * (`avm.iazi.ts`: the allowlist, the shape guard, `schrubbeHinweis`), and the
 * adapter's toetsen parse its output against this schema.
 */
export const avmDataInfoSchema = z.object({
    /** Spec input field names IAZI substituted (allowlisted by the adapter). */
    replaced: z.array(z.string().regex(/^[A-Za-z][A-Za-z0-9]{0,63}$/)).max(50),
    /** The CHECK names IAZI flagged, raw (`Quality…`, shape-guarded). */
    checks: z.array(z.string().regex(/^Quality[A-Z][A-Za-z0-9]{0,62}$/)).max(50),
    replacedUnknown: z.number().int().nonnegative(),
    checksUnknown: z.number().int().nonnegative(),
    /**
     * B11 (IAZI 29-9, answer 11): IAZI's hints in its OWN words (cultureOutput
     * de-CH) — «Mietwert nachhaltig scheint plausibel», the «Kritische Angaben».
     * At most {@link IAZI_HINWEIS_MAX}, each at most
     * {@link IAZI_HINWEIS_MAX_TEKENS} characters, see {@link istSaubererHinweis}.
     * Absent on rows before B11 and when IAZI gave none.
     */
    hinweise: z
        .array(z
        .string()
        .min(1)
        .max(IAZI_HINWEIS_MAX_TEKENS)
        .refine(istSaubererHinweis, 'hinweis: Steuer-/HTML-Zeichen oder halbes Surrogat'))
        .max(IAZI_HINWEIS_MAX)
        .optional(),
    /**
     * B11 (IAZI 29-9, answer 9): inputs WE approximated before the call — never
     * an IAZI substitution. `volume`: an a1 call without a usable Gebäudevolumen
     * sent Nettofläche × 5 with the norm «Andere»; `volume_begrenzt` (only WITH
     * `volume`): that product was above the bound and went at the bound. Each
     * kind at most once; absent when nothing was approximated.
     */
    naeherungen: z
        .array(z.enum(IAZI_NAEHERUNGEN))
        .max(IAZI_NAEHERUNGEN.length)
        .refine((a) => new Set(a).size === a.length, 'naeherungen: doppelt')
        .refine((a) => !a.includes('volume_begrenzt') || a.includes('volume'), 'naeherungen: volume_begrenzt ohne volume')
        .optional(),
});
/**
 * B10 (master 29-9): which of IAZI's two intervals a band carries. 50 = the
 * 50 % interval (`lowConfInt50`/`hiConfInt50`) — since B10 the COMMUNICATED
 * band, extern and intern; 95 = the 95 % interval (`lowConfInt95`/`hiConfInt95`)
 * — the band of every row before B10, the fallback when IAZI sends no usable
 * 50 %, and internally visible as «Statistisches 95 %-Intervall».
 */
export const bandIntervallSchema = z.literal(50).or(z.literal(95));
/** «50 %-Intervall» / «95 %-Intervall»; anything else (NULL = no estimate) → ''. */
export const bandIntervallLabel = (i) => i === 50 ? '50 %-Intervall' : i === 95 ? '95 %-Intervall' : '';
/**
 * Provider-agnostic AVM estimate (stub | iazi | pricehubble). Money is always
 * `currency` (CHF); `confidence` is 0..1; `low <= estimate <= high` (enforced
 * by the provider, not the schema). Shared so the backend AvmProvider and the
 * funnel result screen speak the exact same numbers — see `avm.ts`.
 *
 * B10: `low`/`high` are the COMMUNICATED band — IAZI's 50 % interval when it is
 * usable (`intervall: 50`), else its 95 % interval (`intervall: 95`). The 95 %
 * interval always rides along raw as `low95`/`high95`; `confidence` stays
 * derived from the 95 % interval (continuity) and CRM and PDF no longer show it
 * as «Konfidenz» — the Schätzqualität is `qualityCode`. The four fields are
 * optional so an older answer stays valid; `AvmService` enforces
 * `low95 <= low <= estimate <= high <= high95` when the 95 values are there.
 */
export const valuationEstimateSchema = z.object({
    estimate: z.number().nonnegative(),
    low: z.number().nonnegative(),
    high: z.number().nonnegative(),
    /** B10: IAZI's 95 % interval, raw (`lowConfInt95`). */
    low95: z.number().nonnegative().optional(),
    /** B10: IAZI's 95 % interval, raw (`hiConfInt95`). */
    high95: z.number().nonnegative().optional(),
    /** B10: which interval `low`/`high` carry — see {@link bandIntervallSchema}. */
    intervall: bandIntervallSchema.optional(),
    /** B10: IAZI `seop` (standard error of prediction) when the model sends it; not persisted. */
    seop: z.number().nonnegative().optional(),
    confidence: z.number().min(0).max(1),
    /** ISO 4217 — always 'CHF' for the Swiss market. */
    currency: z.string().min(1),
    /** 'stub' | 'iazi' | 'pricehubble'. */
    provider: z.string().min(1),
    /** ISO-8601 timestamp the provider produced the value. */
    asOf: z.string().datetime(),
    /**
     * B3-3 (16-9): the provider's own quality verdict — IAZI `qualityCode`
     * 0 no estimation · 1 extrapolated · 2 fair · 3 good · 4 prediction with
     * substituted data. Absent for providers without one (stub). Persisted as
     * `avmQualityCode`, written back into the Verkaufsmandat and the base of the
     * band/«indikativ» rule (B3-5).
     */
    qualityCode: z.number().int().min(0).max(4).optional(),
    /** B3-5: the names IAZI replaced / flagged — see {@link avmDataInfoSchema}. */
    dataInfo: avmDataInfoSchema.optional(),
    /** IAZI-order 8-10 A6: what was sent and IAZI's answer (white list) — see `schemas/avm-dossier.ts`. */
    dossier: avmDossierSchema.optional(),
});
// (The pre-fase-B `valuationResultSchema` — point value + pdf flag on the
// wire — is gone: the browser gets `valuationBandResultSchema` above.)
// ── B: Beratungstermin-Buchung (funnel end step) ─────────────────────────────
/**
 * Slot lookup (POST /ingest/booking/slots). Gated exactly like the OTP routes:
 * possession of the unguessable leadId behind the HMAC edge + rate limit
 * (GAP-5 pattern), and the backend additionally requires the lead's phone to
 * be verified — only a completed funnel can book.
 */
export const bookingSlotsRequestSchema = z.object({
    leadId: z.string().uuid(),
});
export const bookingSlotSchema = z.object({
    /** Slot start, ISO-8601 UTC. */
    start: z.string().datetime(),
    durationMin: z.number().int().positive(),
});
export const bookingSlotsReplySchema = z.object({
    slots: z.array(bookingSlotSchema),
});
/** Book one slot (POST /ingest/booking). `start` must be one of the offered
 *  slots — the backend re-validates against a fresh availability window. */
export const bookingCreateSchema = z.object({
    leadId: z.string().uuid(),
    start: z.string().datetime(),
});
export const bookingCreateReplySchema = z.object({
    consultationId: z.string().uuid(),
    /** Confirmed slot start, ISO-8601 UTC (idempotent re-book returns the existing one). */
    scheduledAt: z.string().datetime(),
});
